/**
 * Display derivations for a single column header cell. These are all questions about a column, so
 * they take the column's own metadata rather than an axis and an index.
 */

import {isColumnOutdated, ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import columnDefinitionToIcon from 'notebook/structured-tables/columns/columnDefinitionToIcon';
import {
  StructuredTableMappingsGlobalState,
  StructuredTableMappingsStateManager,
} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsState';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {
  ColumnConstraint,
  InsertableColumnInfo,
  TableColumnType,
} from 'notebook/tables/constants/TableColumnConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {SelectionRange} from 'notebook/tables/constants/TableSelectionConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/** Which side of the selection a column would be inserted on. */
export type InsertDirection = 'BEFORE' | 'AFTER';

/**
 * Whether the column at `index` is one of the leading Single Input columns, which are rendered by
 * `TableSingleInput` rather than by the column header row.
 */
export function computeIsSingleInputColumn(index: number, singleInputBoundaryIndex: number | null): boolean {
  return singleInputBoundaryIndex != null && index < singleInputBoundaryIndex;
}

/**
 * Whether the column header should be highlighted as a user-added custom column. Single Input
 * headers are never highlighted.
 */
export function computeShouldHighlightColumnHeader({
  isSingleInputColumn,
  highlightCustomColumns,
  columnDefinition,
  tableKind,
}: {
  isSingleInputColumn: boolean;
  highlightCustomColumns: boolean | undefined;
  columnDefinition: ColumnDefinition | null;
  tableKind: StructuredTableKind | null;
}): boolean {
  if (isSingleInputColumn) {
    return false;
  }
  return (
    Boolean(highlightCustomColumns) &&
    columnDefinition?.type === TableColumnType.CUSTOM &&
    // Custom Columns should not be highlighted if they have an assigned column group (indicating they have
    // a specific role and were not just added by the user)
    columnDefinition?.columnGroup == null &&
    // Parameter tables use custom cols for datetime values, but these should not be highlighted
    tableKind !== StructuredTableKind.PARAMETER
  );
}

/**
 * Whether the given column is a destination column for at least one table mapping. This reads the
 * global table mappings state, so it must be a selector input rather than a memoized result.
 */
export function computeHasDestinationColumnMapping(
  fileId: string,
  table: Table,
  columnConstraint: ColumnConstraint | null
): boolean {
  const tableId = table.provenanceUuid;
  const columnId = columnConstraint?.structuredTableColumn?.provenanceUuid;

  if (tableId == null || columnId == null) {
    return false;
  }

  const structuredTableMappingsState = StructuredTableMappingsGlobalState.getState(fileId);
  const mappingsForColumn = StructuredTableMappingsStateManager.getMappingsForDestinationColumn(
    structuredTableMappingsState,
    tableId,
    columnId
  );
  return Boolean(mappingsForColumn.length);
}

/**
 * The icon rendered before the column header's label: the column mapping icon for a mapped
 * destination column, otherwise the column type icon. Tables that hide column header icons have no
 * prefix icon.
 */
export function computeColumnHeaderIconPrefix({
  hideColumnHeaderIcons,
  hasDestinationColumnMapping,
  columnDefinition,
}: {
  hideColumnHeaderIcons: boolean | undefined;
  hasDestinationColumnMapping: boolean;
  columnDefinition: ColumnDefinition | null;
}): BenchlingIconName | null {
  if (hideColumnHeaderIcons) {
    return null;
  }
  if (hasDestinationColumnMapping) {
    return 'column-mapping';
  }
  if (columnDefinition) {
    return columnDefinitionToIcon(columnDefinition);
  }
  return null;
}

/** Whether the column is backed by an outdated schema field. */
export function computeColumnIsOutdated(columnDefinition: ColumnDefinition | null): boolean {
  return isColumnOutdated(columnDefinition);
}

/**
 * Whether the selected columns can be deleted from the table.
 *
 * Uses `ColumnConstraint.isColumnUserConfigurable` to decide. If that field is not present on any
 * selected column, falls back to the deprecated `legacyFirstEditableColumnIndex` behavior.
 *
 * Only a selected column can be deleted, since deletion operates on the whole selection range, so
 * a column outside the current column selection (`selectionRange` is null) can never be deleted.
 */
export function computeCanDeleteColumn({
  columnConstraints,
  legacyFirstEditableColumnIndex,
  canAddRemoveColumns,
  selectionRange,
}: {
  columnConstraints: Array<ColumnConstraint | null> | undefined;
  legacyFirstEditableColumnIndex: number | null;
  canAddRemoveColumns: boolean;
  selectionRange: SelectionRange | null;
}): boolean {
  if (!canAddRemoveColumns) {
    return false;
  }
  if (selectionRange == null) {
    return false;
  }

  // If isColumnUserConfigurable is set on any column constraints, it will be set on all of them, so
  // we will rely on it.
  let isColumnUserConfigurableSet = false;
  for (let i = selectionRange.minIndex; i <= selectionRange.maxIndex; i++) {
    const columnConstraint = columnConstraints?.[i];
    if (columnConstraint && columnConstraint.isColumnUserConfigurable != null) {
      isColumnUserConfigurableSet = true;
      if (!columnConstraint.isColumnUserConfigurable) {
        return false;
      }
    }
  }
  if (isColumnUserConfigurableSet) {
    return true;
  }

  return legacyFirstEditableColumnIndex == null || selectionRange.minIndex >= legacyFirstEditableColumnIndex;
}

/**
 * Whether a column can be inserted on the given side of the selected columns.
 *
 * If `ColumnConstraint.isColumnUserConfigurable` is present on any selected column, it overrides
 * the deprecated `legacyFirstEditableColumnIndex` behavior.
 *
 * This is shared between the regular column headers and Single Inputs so that whether the "Insert
 * column/single input left/right" option is enabled stays identical between the two. Single Inputs
 * pass the Single-Input-compatible subset of the insertable columns.
 */
export function computeCanInsertColumn({
  columnConstraints,
  legacyFirstEditableColumnIndex,
  disallowCustomColumns,
  canAddRemoveColumns,
  insertableColumnInfos,
  selectionRange,
  direction,
}: {
  columnConstraints: Array<ColumnConstraint | null> | undefined;
  legacyFirstEditableColumnIndex: number | null;
  disallowCustomColumns: boolean;
  canAddRemoveColumns: boolean;
  insertableColumnInfos: Array<InsertableColumnInfo> | undefined;
  selectionRange: SelectionRange | null;
  direction: InsertDirection;
}): boolean {
  // short circuit checks on table config
  if (!canAddRemoveColumns || (disallowCustomColumns && (insertableColumnInfos?.length ?? 0) === 0)) {
    return false;
  }

  if (selectionRange == null) {
    return false;
  }

  // check whether any selected column sets isColumnUserConfigurable
  for (let i = selectionRange.minIndex; i <= selectionRange.maxIndex; i++) {
    const columnConstraint = columnConstraints?.[i];
    if (columnConstraint && columnConstraint.isColumnUserConfigurable != null) {
      return true;
    }
  }

  // Fall back to deprecated firstEditableColumnIndex behavior
  if (legacyFirstEditableColumnIndex != null) {
    const newIndex = direction === 'BEFORE' ? selectionRange.minIndex : selectionRange.maxIndex + 1;
    if (newIndex < legacyFirstEditableColumnIndex) {
      return false;
    }
  }

  return true;
}
